// src/pages/AdminPanel.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { fetchUserStatsById, fetchTotalStats } from '../services/api';

// Read your backend URL from the environment variable
const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

const AdminPanel = () => {
  const { user, role, getToken } = useAuth();
  const navigate = useNavigate();

  const [superUsers, setSuperUsers] = useState([]);
  const [promoteEmail, setPromoteEmail] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  // Add total stats state
  const [totalStats, setTotalStats] = useState(null);
  const [loadingTotalStats, setLoadingTotalStats] = useState(false);

  useEffect(() => {
    if (role !== 'admin') {
      navigate('/');
    }
  }, [role, navigate]);

  useEffect(() => {
    if (role === 'admin' && user?.id) {
      fetchSuperUsers();
      fetchAllTotalStats();
    }
  }, [role, user?.id]);

  const fetchSuperUsers = async () => {
    try {
      const token = getToken();
      const res = await axios.get(`${API_BASE}/api/admin/super-users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Safety check — ensure the result is an array
      if (Array.isArray(res.data)) {
        setSuperUsers(res.data);
      } else {
        console.warn('[AdminPanel] /super-users returned non-array:', res.data);
        setSuperUsers([]);
      }
    } catch (err) {
      console.error('[AdminPanel] Error fetching super users:', err);
      setSuperUsers([]);
    }
  };

  const fetchUserStats = async (userId) => {
    if (!userId) return;
    
    setLoadingStats(true);
    try {
      const token = getToken();
      const stats = await fetchUserStatsById(token, userId);
      setUserStats(stats);
    } catch (err) {
      console.error('[AdminPanel] Error fetching user stats:', err);
    } finally {
      setLoadingStats(false);
    }
  };

  // Add function to fetch total stats
  const fetchAllTotalStats = async () => {
    setLoadingTotalStats(true);
    try {
      const token = getToken();
      const stats = await fetchTotalStats(token);
      setTotalStats(stats);
    } catch (err) {
      console.error('[AdminPanel] Error fetching total stats:', err);
    } finally {
      setLoadingTotalStats(false);
    }
  };

  const handlePromote = async (e) => {
    e.preventDefault();
    if (!promoteEmail || role !== 'admin') return;
    try {
      const token = getToken();
      await axios.post(
        `${API_BASE}/api/admin/promote-super-user`,
        { email: promoteEmail },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      alert(`${promoteEmail} has been promoted to super_user.`);
      setPromoteEmail('');
      fetchSuperUsers();
    } catch (err) {
      console.error('[AdminPanel] Error promoting user:', err);
      alert('Error promoting user. Check console for details.');
    }
  };

  const handleAutoPairings = async () => {
    try {
      const token = getToken();
      await axios.post(
        `${API_BASE}/api/admin/autoPairingsRun`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      alert('Auto-pairing job triggered!');
    } catch (err) {
      console.error('[AdminPanel] Error triggering auto-pairings:', err);
      alert('Error triggering auto-pairings. Check console for details.');
    }
  };

  const viewUserStats = (u) => {
    setSelectedUser(u);
    fetchUserStats(u.id);
  };

  return (
    <div className="container" style={{ marginTop: '1rem' }}>
      <h2>Admin Panel</h2>

      {/* User Stats Section - Updated with user email in heading */}
      {selectedUser && (
        <div style={{ 
          marginBottom: '2rem', 
          padding: '1rem', 
          backgroundColor: 'var(--dynamic-secondary-background)', 
          borderRadius: '8px', 
          color: 'var(--dynamic-text)'
        }}>
          <h3>User Stats: {selectedUser.email || selectedUser.id}</h3>
          {loadingStats ? (
            <p style={{ color: 'var(--dynamic-secondary-text)' }}>Loading stats...</p>
          ) : userStats ? (
            <div>
              <p>Pairings created: {userStats.pairingsCreated}</p>
              <p>Pairings voted on: {userStats.pairingsVotedOn}</p>
              <p>Total contributions: {userStats.pairingsCreated + userStats.pairingsVotedOn}</p>
              <button 
                onClick={() => setSelectedUser(null)} 
                style={{ 
                  marginTop: '10px',
                  padding: '6px 12px',
                  background: 'var(--dynamic-background)',
                  color: 'var(--dynamic-text)',
                  border: '1px solid var(--dynamic-secondary-text)',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          ) : (
            <p style={{ color: 'var(--dynamic-secondary-text)' }}>No stats available</p>
          )}
        </div>
      )}

      <h3>Current Super Users</h3>
      {superUsers.length ? (
        <ul style={{ 
          listStyle: 'none', 
          padding: 0,
          marginBottom: '2rem'
        }}>
          {superUsers.map((u) => (
            <li key={u.id} style={{ 
              padding: '12px 0', 
              borderBottom: '1px solid var(--subtle-accent)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: 'var(--dynamic-text)'
            }}>
              <span>{u.email || u.id}</span>
              <button 
                onClick={() => viewUserStats(u)}
                style={{
                  padding: '6px 12px',
                  backgroundColor: 'var(--dynamic-secondary-background)',
                  color: 'var(--dynamic-text)',
                  border: '1px solid var(--dynamic-secondary-text)',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                View Stats
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p style={{ 
          marginBottom: '2rem',
          color: 'var(--dynamic-secondary-text)'
        }}>
          No super users found.
        </p>
      )}

      <h3>Promote a User to Super User</h3>
      <form 
        onSubmit={handlePromote}
        style={{ marginBottom: '2rem' }}
      >
        <input
          type="email"
          placeholder="User's email"
          value={promoteEmail}
          onChange={(e) => setPromoteEmail(e.target.value)}
          style={{ 
            marginRight: '0.5rem',
            padding: '8px',
            backgroundColor: 'var(--dynamic-background)',
            color: 'var(--dynamic-text)',
            border: '1px solid var(--dynamic-secondary-text)',
            borderRadius: '4px'
          }}
        />
        <button 
          type="submit"
          style={{
            padding: '8px 16px',
            backgroundColor: 'var(--dynamic-background)',
            color: 'var(--dynamic-text)',
            border: '1px solid var(--dynamic-secondary-text)',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Promote
        </button>
      </form>

      <h3>Auto-Pairing Job</h3>
      <button 
        onClick={handleAutoPairings}
        style={{
          padding: '8px 16px',
          backgroundColor: 'var(--dynamic-background)',
          color: 'var(--dynamic-text)',
          border: '1px solid var(--dynamic-secondary-text)',
          borderRadius: '4px',
          cursor: 'pointer',
          marginBottom: '2rem'
        }}
      >
        Run Auto-Pairings
      </button>

      {/* New Total Stats Section */}
      <h3>Application Stats</h3>
      <div style={{ 
        padding: '1rem', 
        backgroundColor: 'var(--dynamic-secondary-background)', 
        borderRadius: '8px', 
        color: 'var(--dynamic-text)',
        marginBottom: '2rem'
      }}>
        {loadingTotalStats ? (
          <p style={{ color: 'var(--dynamic-secondary-text)' }}>Loading stats...</p>
        ) : totalStats ? (
          <div>
            <p>Total Places: {totalStats.totalPlaces}</p>
            <p>Total Pairings: {totalStats.totalPairings}</p>
            <p>Total Votes: {totalStats.totalVotes}</p>
            <button 
              onClick={fetchAllTotalStats} 
              style={{ 
                marginTop: '10px',
                padding: '6px 12px',
                background: 'var(--dynamic-background)',
                color: 'var(--dynamic-text)',
                border: '1px solid var(--dynamic-secondary-text)',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Refresh
            </button>
          </div>
        ) : (
          <p style={{ color: 'var(--dynamic-secondary-text)' }}>No stats available</p>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;